import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { useState, useEffect, useMemo } from 'react'
import { fetchContent, subscribeToType, LIVE_MODE } from '../lib/sanity'
import { PortableText, type PortableTextComponents } from '@portabletext/react'

interface JourneyContent {
  heading: string
  content: any[]
  image: {
    asset: {
      url: string
    }
    alt: string
  }
}

const storyPills = ['GLP-1 Peptides', 'Metabolic Reset', 'Mindful Fueling']

const fallbackParagraphs = [
  'Andrea Villalobos offers a calm, practical space for people who want more consistency while working toward their longevity goals.',
  'Her remote support focuses on routines, accountability, and lifestyle habits for people using GLP-1 medication with guidance from their licensed clinical care team.',
  'This is non-medical support. Andrea does not diagnose, prescribe, or manage treatment.'
]

const Journey = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const [content, setContent] = useState<JourneyContent | null>(null)

  useEffect(() => {
    let unsubscribe: (() => void) | undefined
    const getJourneyContent = async () => {
      try {
        const [journeyData] = await fetchContent('journey')
        setContent(journeyData)
        if (LIVE_MODE) {
          unsubscribe = subscribeToType('journey', (docs) => setContent(docs[0]))
        }
      } catch (error) {
        console.error('Error fetching journey content:', error)
      }
    }

    getJourneyContent()
    return () => {
      if (unsubscribe) unsubscribe()
    }
  }, [])

  const portableComponents: PortableTextComponents = useMemo(() => ({
    block: {
      normal: ({ children }) => (
        <p className="text-base md:text-lg text-slate-700 leading-relaxed">{children}</p>
      ),
    },
  }), [])

  return (
    <section id="journey-section" className="relative isolate overflow-hidden bg-[#cdfbf1] py-24 text-slate-900">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.9),_rgba(205,251,241,0.6))]" />
      <div className="absolute -right-10 top-12 h-64 w-64 rounded-full bg-white/60 blur-[140px]" />
      <div className="absolute -left-24 bottom-0 h-56 w-56 rounded-full bg-white/40 blur-[120px]" />

      <div className="container relative z-10 mx-auto px-6">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 60 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="grid items-center gap-12 lg:grid-cols-[1.1fr_0.9fr]"
        >
          <div className="space-y-10">
            <div className="flex flex-wrap gap-3 text-xs uppercase tracking-[0.3em] text-teal-700">
              <span className="rounded-full border border-teal-200 px-4 py-1 text-[11px] font-semibold text-slate-800 bg-white/80">Longevity Studio</span>
              <span className="rounded-full border border-teal-200 px-4 py-1 text-[11px] font-semibold text-slate-800 bg-white/60">GLP-1 Concierge</span>
            </div>

            <div className="space-y-6">
              <h1 className="text-4xl sm:text-5xl xl:text-6xl font-semibold leading-tight text-slate-900">
                {content?.heading || 'Remote GLP-1 accountability support for your longevity goals'}
              </h1>
              <div className="space-y-4 text-lg text-slate-700 leading-relaxed">
                {content?.content ? (
                  <PortableText value={content.content} components={portableComponents} />
                ) : (
                  fallbackParagraphs.map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              {storyPills.map((pill) => (
                <span
                  key={pill}
                  className="rounded-full border border-teal-300 bg-white px-4 py-2 text-sm font-medium text-slate-800"
                >
                  {pill}
                </span>
              ))}
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 80 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="absolute -inset-4 rounded-[32px] bg-gradient-to-br from-white/70 via-[#cdfbf1] to-transparent blur-2xl" />
            <div className="relative rounded-[32px] border border-white/70 bg-white p-3 shadow-[0_25px_70px_rgba(6,96,94,0.15)]">
              <div className="overflow-hidden rounded-[24px]">
                <img
                  src={content?.image?.asset?.url || '/images/person-andrea-smiling.png'}
                  alt={content?.image?.alt || 'Andrea Villalobos smiling'}
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="mt-5 flex items-center justify-between rounded-2xl bg-[#cdfbf1] px-5 py-4 text-sm text-slate-700">
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-teal-700">Concierge onboarding</p>
                  <p className="text-base font-semibold text-slate-900">Fully guided in under 10 minutes</p>
                </div>
                <span className="whitespace-nowrap rounded-full bg-white px-4 py-1 text-xs font-semibold text-teal-700">GLP-1</span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}

export default Journey
